import { Terminal, Loader2, RefreshCw, Search, UserCheck } from 'lucide-react';
import { useEffect, useState } from 'react';

import { api } from '@/lib/api';

interface ActivityLogItem {
  id: string;
  event: string;
  actor: string;
  target?: string;
  timestamp: string;
}

export function AuditTrail() {
  const [logs, setLogs] = useState<ActivityLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  const fetchLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/super/activity-logs');
      setLogs(response.data.data);
    } catch (err: any) {
      setError('Failed to fetch administrative activity audit logs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const filteredLogs = logs.filter(
    (log) =>
      log.event.toLowerCase().includes(search.toLowerCase()) ||
      log.actor.toLowerCase().includes(search.toLowerCase()) ||
      (log.target && log.target.toLowerCase().includes(search.toLowerCase())),
  );

  return (
    <div className="space-y-6 font-sans text-slate-800 antialiased">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-100 pb-4">
        <div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 text-xs font-semibold mb-2">
            <UserCheck size={12} />
            Compliance & Security
          </div>
          <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">
            Administrative Audit Trail
          </h1>
          <p className="text-sm text-slate-500">
            Trace historical and real-time operations triggered by SaaS Administrators and Shop
            Owners.
          </p>
        </div>

        <button
          onClick={fetchLogs}
          disabled={loading}
          className="btn-primary flex items-center gap-1.5 self-start sm:self-center px-4 py-2 border border-emerald-600 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-500 disabled:opacity-50 cursor-pointer shadow-sm transition-all"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          <span>Refresh Audit Logs</span>
        </button>
      </div>

      {error && (
        <div className="p-4 bg-rose-50 border border-rose-150 text-xs text-rose-700 font-semibold rounded-xl">
          {error}
        </div>
      )}

      {/* Control Actions Bar */}
      <div className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Filter logs by event name, actor email, or target shop..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="block w-full pl-9 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-slate-50 focus:outline-none focus:bg-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 transition-all font-medium"
          />
        </div>
      </div>

      {/* Main Logs Table Grid */}
      <div className="card bg-white border border-slate-100 p-5 rounded-xl shadow-sm space-y-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 text-slate-400">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mb-2" />
            <span className="text-xs font-semibold">Retrieving platform security ledger...</span>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            <Terminal className="mx-auto mb-3 opacity-30 text-emerald-600" size={40} />
            <p className="text-xs font-bold">
              No administrative log entries found matching criteria.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-slate-100 rounded-lg">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-100 text-slate-400 font-bold uppercase tracking-wider bg-slate-50/50">
                  <th className="py-3 px-4 text-center w-12">S.No.</th>
                  <th className="py-3 px-4">Action Event description</th>
                  <th className="py-3 px-3">Triggered By (Actor)</th>
                  <th className="py-3 px-3">Target Reference</th>
                  <th className="py-3 px-4 text-right">Log Timestamp</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-650 font-medium">
                {filteredLogs.map((log, idx) => (
                  <tr key={log.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="py-3.5 px-4 text-center text-slate-500 font-medium">{idx + 1}</td>
                    <td className="py-3.5 px-4 font-bold text-slate-800">{log.event}</td>
                    <td className="py-3.5 px-3">
                      <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-bold border border-emerald-100">
                        {log.actor}
                      </span>
                    </td>
                    <td className="py-3.5 px-3 font-mono text-[11px] text-slate-500">
                      {log.target || 'N/A'}
                    </td>
                    <td className="py-3.5 px-4 text-right text-slate-400 text-[11px] font-mono">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
